import React, { useEffect, useRef } from "react";
import { Eye, Lock } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useNexOs } from "@/lib/nexOs";
import { isSuperAdminUser } from "@/lib/placement";

const SKIP_KINDS = new Set(["open_module", "quickForms", "quickEdit", "cardConfig", "step_coaching"]);
const WRITE_RE = /^\s*(\+|save|add|new|create|delete|remove|post|submit|void|verify|approve|reject|sign|upload|edit|move|pay|record|assign|unassign|archive|unarchive|mark|flag|send|apply|confirm|book|restore|issue|reset|correct|defer|hand out|update|import|publish|schedule|reschedule|cancel event|check in)\b/i;

/** Highest level the user has on any card that opens this window kind: "edit" | "view" | "none" | null (no gate). */
export function levelForKind(user, schema, kind) {
  if (!user || isSuperAdminUser(user) || !user.card_access || SKIP_KINDS.has(kind)) return null;
  const ids = (schema?.capabilities_template || []).filter((c) => c.modal_kind === kind).map((c) => c.id);
  if (!ids.length) return null;
  const lv = ids.map((id) => user.card_access[id]);
  return lv.includes("edit") ? "edit" : lv.includes("view") ? "view" : "none";
}

export function useWindowLevel(kind) {
  const { user } = useAuth();
  const { schema } = useNexOs();
  return levelForKind(user, schema, kind);
}

function useReadOnlyGuard(ref, on) {
  useEffect(() => {
    const root = ref.current;
    if (!on || !root) return undefined;
    let raf = 0;
    const sweep = () => {
      raf = 0;
      root.querySelectorAll("button, input[type=submit]").forEach((el) => {
        if (el.disabled || el.getAttribute("role") === "tab" || el.closest("[data-readonly-ok]")) return;
        if (el.type === "submit" || WRITE_RE.test(el.textContent || el.value || el.getAttribute("aria-label") || "")) {
          el.disabled = true;
          el.title = "Read only on this card";
          el.setAttribute("data-readonly-locked", "true");
        }
      });
    };
    const obs = new MutationObserver(() => { if (!raf) raf = requestAnimationFrame(sweep); });
    obs.observe(root, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ["disabled"] });
    sweep();
    return () => { obs.disconnect(); if (raf) cancelAnimationFrame(raf); };
  }, [ref, on]);
}

export function CardAccessGate({ kind, children }) {
  const level = useWindowLevel(kind);
  const ref = useRef(null);
  useReadOnlyGuard(ref, level === "view");
  if (level === "none") {
    return (
      <div data-testid="card-access-none" className="h-full grid place-items-center p-8 text-center">
        <div className="space-y-2"><Lock className="w-6 h-6 mx-auto text-nex-muted" /><p className="text-sm text-nex-silver">You don't have access to this card.</p>
          <p className="text-xs text-nex-muted">Ask a Super Admin to tick See for you in SA Controls › Access matrix.</p></div>
      </div>
    );
  }
  if (level !== "view") return children;
  return (
    <div ref={ref} data-testid="card-access-readonly" className="flex flex-col h-full">
      <div data-testid="card-access-readonly-banner" className="shrink-0 flex items-center gap-2 px-4 py-1.5 bg-sky-500/10 border-b border-sky-400/20 text-[11px] text-sky-200">
        <Eye className="w-3.5 h-3.5" />Read only. You can look, but you can't change anything on this card.
      </div>
      <div className="flex-1 min-h-0">{children}</div>
    </div>
  );
}
